import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import { Response } from '../services/backendService/types';

const STALE_TIME = 10 * 60 * 1000; // 10 minutes

const useResponses = (
  gameId: string,
  slideId: string,
  filterDismissed?: boolean,
  tenantId?: string,
) => {
  const { gameAPI } = useContext(SocketContext);
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ['responses', tenantId, gameId, slideId, filterDismissed],
    [tenantId, gameId, slideId, filterDismissed],
  );

  // Subscribe to real-time updates and populate the query cache
  useEffect(() => {
    if (!gameId || !gameAPI || !tenantId || !slideId) return;

    const sub = gameAPI.game
      .onRoundResponsesChanged({ tenantId, gameId, slideId })
      .subscribe((responses: Response[]) => {
        const filteredResponses = filterDismissed
          ? responses.filter(r => !r.dismissed)
          : responses;
        queryClient.setQueryData<Response[]>(queryKey, filteredResponses);
      });

    return () => {
      sub.unsubscribe();
    };
  }, [gameId, slideId, filterDismissed, gameAPI, tenantId, queryClient, queryKey]);

  const { data } = useQuery<Response[] | undefined>({
    queryKey,
    queryFn: () => Promise.resolve(undefined),
    staleTime: STALE_TIME,
    enabled: Boolean(gameId && gameAPI && tenantId),
  });

  const isLoading = data === undefined;
  const responses = useMemo(() => data ?? [], [data]);

  const localDismiss = useCallback(
    ({ responseId, dismiss }: { responseId: string; dismiss: boolean }) => {
      queryClient.setQueryData<Response[]>(
        queryKey,
        oldResponses =>
          oldResponses?.map(res =>
            res.id === responseId ? { ...res, dismissed: dismiss } : res,
          ) ?? [],
      );
    },
    [queryClient, queryKey],
  );

  const wordCount = useMemo(() => {
    //count all words in the responses
    return responses.reduce((acc, res) => {
      return acc + res.response.split(/\s+/).filter(word => word.length > 0).length;
    }, 0);
  }, [responses]);

  const detailCount = useMemo(() => {
    return responses.reduce((acc, res) => {
      //drawingDetails is a number
      if (res.drawingDetails) {
        return acc + res.drawingDetails;
      }
      return acc;
    }, 0);
  }, [responses]);

  return {
    count: responses.length,
    wordCount,
    detailCount,
    responses,
    isLoading,
    finishedResponses: responses.filter(r => r.isFinished).length,
    dismissResponse: localDismiss,
  };
};

export default useResponses;
